/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { FC, useEffect } from "react";

import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { PaginatedList } from "@/components/templates";
import { FeaturePageState, InsuredGood, InsuredGoodCategoryType, Permissions } from "@/shared/domain";
import { useAuth, useGlobalFilter, useNavigation, useRouterHook } from "@/shared/hooks";

import InsuredGoodItem from "../components/InsuredGoodItem/InsuredGoodItem";
import InsuredGoodTable from "../components/InsuredGoodTable/InsuredGoodTable";
import { useInsuredGoodsQueries } from "../utils";

const getCategoryFilterOptions = (t: TFunction, insuredGoodsCategories: MyBrokerApi.CategoryCounters[]) => {
    let totalCount = 0;
    const filterOptions = insuredGoodsCategories.map((x) => {
        totalCount += x.totalItems;
        return { id: x.category.id, label: x.category.name, quantity: x.totalItems };
    });

    const categories = [...filterOptions];
    if (filterOptions.length > 1) {
        categories.unshift({
            id: InsuredGoodCategoryType.ALL,
            label: t("Common.All"),
            quantity: totalCount,
        });
    }

    return categories;
};

const InsuredGoodPage: FC = () => {
    const {
        state: { filterId },
    } = useRouterHook<FeaturePageState>();
    const { user } = useAuth();
    const { t } = useTranslation();
    const { policyholdersGF } = useGlobalFilter();
    const { navToFeatureDocuments, navToInsuredGoodSearch } = useNavigation();

    const { tableMode, useInsuredGoodsPageQuery, useInsuredGoodsCategoriesQuery } = useInsuredGoodsQueries();
    const {
        page,
        setPage,
        category,
        isFetching,
        setCategory,
        totalOfPages,
        result: insuredGoods,
        orderBy,
        handleSort,
        handlePageSizeChange,
    } = useInsuredGoodsPageQuery({ defaultCategory: filterId || InsuredGoodCategoryType.ALL });

    const { insuredGoodCategories, isLoadingCategories } = useInsuredGoodsCategoriesQuery({ enabled: true });

    const availableFilters = getCategoryFilterOptions(t, insuredGoodCategories);

    useEffect(() => {
        if (insuredGoodCategories.length === 1 && insuredGoodCategories[0].category.id !== category) {
            setCategory(insuredGoodCategories[0].category.id);
            setPage(1);
        }
    }, [insuredGoodCategories]);

    const renderItem = (insuredGood) => (
        <InsuredGoodItem key={`insuredGoodItem-${insuredGood.id}`} insuredGood={insuredGood} />
    );

    const renderTable = (items: InsuredGood[]) => (
        <InsuredGoodTable insuredGoods={items} orderBy={orderBy} handleSort={handleSort} />
    );

    return (
        <PaginatedList<InsuredGood>
            page={page}
            tableMode={tableMode}
            emptyListMessage={t("InsuredGoods.Empty")}
            pageTitle={t("PageTitles.InsuredGoods")}
            renderItem={renderItem}
            renderTable={renderTable}
            isFetchingList={isFetching}
            isLoading={isLoadingCategories || isFetching}
            setPage={setPage}
            category={category}
            totalOfPages={totalOfPages}
            availableFilters={availableFilters}
            setCategory={setCategory}
            items={insuredGoods}
            handlePageSizeChange={handlePageSizeChange}
            openSearch={navToInsuredGoodSearch}
            openLinkedDocuments={() => navToFeatureDocuments("insuredGoods")}
            displayLinkedDocumentsButton={user.someHasPermission(Permissions.ViewLinkedDocuments, policyholdersGF)}
        />
    );
};

export default InsuredGoodPage;
